/**
 * What a publish of the visible range touches (#2038): the employees with drafts, each
 * with their draft count, and the shifts already published that changed afterwards.
 */
import type { Shift } from '@/types/shifts';
import { ShiftStatus } from '@/types/shifts';
import { isChangedAfterPublish } from './shiftLifecycle';

export interface PublishEmployeeSummary {
  employeeId: string;
  name: string;
  draftCount: number;
}

export interface PublishSummary {
  employees: PublishEmployeeSummary[];
  changed: Shift[];
  /** Open shifts among the drafts (#2040): nobody gets a push, the groups see them. */
  openDrafts: number;
}

function employeeName(shift: Shift): string {
  const name = `${shift.employee?.firstname ?? ''} ${shift.employee?.lastname ?? ''}`.trim();
  return name || (shift.employee_id ?? '');
}

export function summarizePublish(shifts: Shift[]): PublishSummary {
  const byEmployee = new Map<string, PublishEmployeeSummary>();
  const changed: Shift[] = [];
  let openDrafts = 0;
  for (const shift of shifts) {
    if (shift.status === ShiftStatus.DRAFT && !shift.employee_id) {
      openDrafts += 1;
    } else if (shift.status === ShiftStatus.DRAFT) {
      const employeeId = shift.employee_id ?? '';
      const entry = byEmployee.get(employeeId) ?? {
        employeeId,
        name: employeeName(shift),
        draftCount: 0,
      };
      entry.draftCount += 1;
      byEmployee.set(employeeId, entry);
    } else if (isChangedAfterPublish(shift)) {
      changed.push(shift);
    }
  }
  const employees = Array.from(byEmployee.values()).sort((a, b) => a.name.localeCompare(b.name));
  changed.sort(
    (a, b) =>
      (a.shift_date ?? '').localeCompare(b.shift_date ?? '') ||
      employeeName(a).localeCompare(employeeName(b)) ||
      (a.start_time ?? '').localeCompare(b.start_time ?? '')
  );
  return { employees, changed, openDrafts };
}

export { employeeName as publishEmployeeName };
